/**
 * Client-side leeftijdsgroep-aggregation. Each mapping turns single-year ages
 * into a bucket label. The youngest band ("0-4" / "0-3") includes age `-1`
 * (born during the year).
 */

export type DoelgroepKey = '5jaar' | 'onderwijs' | 'doelgroep'

const range = (start: number, endExclusive: number): number[] =>
  Array.from({ length: endExclusive - start }, (_, i) => start + i)

interface BucketDef {
  label: string
  ages: number[]
}

const FIVE_YEAR_BUCKETS: BucketDef[] = [
  { label: '0-4', ages: range(-1, 5) },
  { label: '5-9', ages: range(5, 10) },
  { label: '10-14', ages: range(10, 15) },
  { label: '15-19', ages: range(15, 20) },
  { label: '20-24', ages: range(20, 25) },
  { label: '25-29', ages: range(25, 30) },
  { label: '30-34', ages: range(30, 35) },
  { label: '35-39', ages: range(35, 40) },
  { label: '40-44', ages: range(40, 45) },
  { label: '45-49', ages: range(45, 50) },
  { label: '50-54', ages: range(50, 55) },
  { label: '55-59', ages: range(55, 60) },
  { label: '60-64', ages: range(60, 65) },
  { label: '65-69', ages: range(65, 70) },
  { label: '70-74', ages: range(70, 75) },
  { label: '75-79', ages: range(75, 80) },
  { label: '80-84', ages: range(80, 85) },
  { label: '85-89', ages: range(85, 90) },
  { label: '90-94', ages: range(90, 95) },
  { label: '95+', ages: range(95, 110) },
]

const ONDERWIJS_BUCKETS: BucketDef[] = [
  { label: '4', ages: [4] },
  { label: '4-5', ages: range(4, 6) },
  { label: '4-6', ages: range(4, 7) },
  { label: '4-12', ages: range(4, 13) },
  { label: '12-13', ages: range(12, 14) },
  { label: '13-16', ages: range(13, 17) },
  { label: '13-18', ages: range(13, 19) },
]

const DOELGROEP_BUCKETS: BucketDef[] = [
  { label: '0-3', ages: range(-1, 4) },
  { label: '4-11', ages: range(4, 12) },
  { label: '12-17', ages: range(12, 18) },
  { label: '18-23', ages: range(18, 24) },
  { label: '24-54', ages: range(24, 55) },
  { label: '55-64', ages: range(55, 65) },
  { label: '65-74', ages: range(65, 75) },
  { label: '75+', ages: range(75, 110) },
]

const DOELGROEP_BUCKETS_BY_KEY: Record<DoelgroepKey, BucketDef[]> = {
  '5jaar': FIVE_YEAR_BUCKETS,
  'onderwijs': ONDERWIJS_BUCKETS,
  'doelgroep': DOELGROEP_BUCKETS,
}

export const DOELGROEP_LABELS: Record<DoelgroepKey, string> = {
  '5jaar': '5-jaars groep',
  'onderwijs': 'Onderwijs groep',
  'doelgroep': 'Doelgroep',
}

interface PivotRowLike {
  leeftijd: number | string
  geslacht?: string
  bron?: string
  [yearOrOther: string]: number | string | undefined | null
}

/**
 * Bucket pivot rows by a doelgroep mapping. Rows whose ``leeftijd`` is not in
 * any bucket are dropped — only ages present in the mapping appear.
 * Rows that share the same (bucket, geslacht, bron) combination are summed.
 *
 * The "Totaal"-row is preserved verbatim — it carries pre-aggregated totals
 * that should remain on screen regardless of bucketing.
 */
export const aggregateByDoelgroep = (
  rows: PivotRowLike[],
  doelgroep: DoelgroepKey | '',
  yearColumns: string[],
): PivotRowLike[] => {
  if (!doelgroep) return rows
  const buckets = DOELGROEP_BUCKETS_BY_KEY[doelgroep]
  const totalRow = rows.find((r) => r.leeftijd === 'Totaal')
  const dataRows = rows.filter((r) => r.leeftijd !== 'Totaal')

  const ageToBucket = new Map<number, string>()
  buckets.forEach((b) => b.ages.forEach((age) => ageToBucket.set(age, b.label)))

  // Sum across geslacht + bron so only one row per leeftijdsgroep remains.
  // Fresh objects per bucket so we never alias React-state rows back in.
  const grouped = new Map<string, PivotRowLike>()
  for (const row of dataRows) {
    const ageNum = typeof row.leeftijd === 'number' ? row.leeftijd : Number(row.leeftijd)
    const bucket = ageToBucket.get(ageNum)
    if (!bucket) continue
    const existing = grouped.get(bucket)
    if (existing) {
      for (const year of yearColumns) {
        const a = typeof existing[year] === 'number' ? (existing[year] as number) : 0
        const b = typeof row[year] === 'number' ? (row[year] as number) : 0
        existing[year] = a + b
      }
    } else {
      const fresh: PivotRowLike = { leeftijd: bucket }
      for (const year of yearColumns) {
        fresh[year] = typeof row[year] === 'number' ? (row[year] as number) : 0
      }
      grouped.set(bucket, fresh)
    }
  }

  // Preserve bucket-order from the mapping.
  const bucketOrder = new Map<string, number>(buckets.map((b, idx): [string, number] => [b.label, idx]))
  const aggregated = Array.from(grouped.values()).sort((a, b) => {
    return (bucketOrder.get(String(a.leeftijd)) ?? 0) - (bucketOrder.get(String(b.leeftijd)) ?? 0)
  })

  return totalRow ? [...aggregated, totalRow] : aggregated
}
